import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { getSystemLogs, SystemLogQuery } from "@utils/api-communication/RestApiCli";
import { parseDateOnlyOrNull } from "@utils/CommonUtil";

interface SystemLogEntry {
  _id?: string;
  data?: string;
  createdAt?: string;
  pid?: string;
  username?: string;
  orgId?: string;
  auto?: boolean;
}

type AutoFilter = "any" | "auto" | "user";

interface Filters {
  code: string;
  pid: string;
  username: string;
  orgId: string;
  auto: AutoFilter;
  from: Date | null;
  to: Date | null;
}

const EMPTY_FILTERS: Filters = {
  code: "",
  pid: "",
  username: "",
  orgId: "",
  auto: "any",
  from: null,
  to: null,
};

const LOG_CODES: { value: string; label: string }[] = [
  { value: "", label: "Any code" },
  { value: "PasswordResetByAdmin", label: "PasswordResetByAdmin" },
  { value: "ProductConfigCreated", label: "ProductConfigCreated" },
];

const AUTO_OPTIONS: { value: AutoFilter; label: string }[] = [
  { value: "any", label: "Any actor" },
  { value: "user", label: "User-triggered" },
  { value: "auto", label: "Automatic" },
];

const PAGE_SIZE = 25;

const toQuery = (filters: Filters, page: number): SystemLogQuery => ({
  page,
  limit: PAGE_SIZE,
  ...(filters.code ? { code: filters.code } : {}),
  ...(filters.pid ? { pid: filters.pid.trim() } : {}),
  ...(filters.username ? { username: filters.username.trim() } : {}),
  ...(filters.orgId ? { orgId: filters.orgId.trim() } : {}),
  ...(filters.auto === "any" ? {} : { auto: filters.auto === "auto" }),
  ...(filters.from ? { from: filters.from.toISOString() } : {}),
  ...(filters.to ? { to: filters.to.toISOString() } : {}),
});

const SystemLogs: React.FC = () => {
  const [logs, setLogs] = useState<SystemLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingFilters, setPendingFilters] = useState<Filters>(EMPTY_FILTERS);
  const [activeFilters, setActiveFilters] = useState<Filters>(EMPTY_FILTERS);

  const fetchLogs = (nextPage: number, filters: Filters) => {
    setLoading(true);
    setError(null);
    getSystemLogs(
      toQuery(filters, nextPage),
      (response) => {
        const data = response?.data ?? {};
        const items: SystemLogEntry[] = Array.isArray(data.data) ? data.data : [];
        setLogs(items);
        setTotal(typeof data.total === "number" ? data.total : items.length);
        setLoading(false);
      },
      () => {
        setError("Failed to fetch system logs");
        setLogs([]);
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    fetchLogs(page, activeFilters);
  }, [page]);

  const applyFilters = () => {
    setActiveFilters(pendingFilters);
    if (page === 1) {
      fetchLogs(1, pendingFilters);
    } else {
      setPage(1);
    }
  };

  const clearFilters = () => {
    setPendingFilters(EMPTY_FILTERS);
    setActiveFilters(EMPTY_FILTERS);
    if (page === 1) {
      fetchLogs(1, EMPTY_FILTERS);
    } else {
      setPage(1);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Container size="lg">
      <Title order={2} my="md">
        System Logs
      </Title>

      <Paper withBorder radius="sm" p="md" mb="md">
        <Stack gap="sm">
          <Group grow>
            <Select
              label="Event code"
              data={LOG_CODES}
              value={pendingFilters.code}
              onChange={(v) => setPendingFilters({ ...pendingFilters, code: v ?? "" })}
              allowDeselect={false}
            />
            <Select
              label="Actor"
              data={AUTO_OPTIONS}
              value={pendingFilters.auto}
              onChange={(v) =>
                setPendingFilters({ ...pendingFilters, auto: (v as AutoFilter) ?? "any" })
              }
              allowDeselect={false}
            />
          </Group>
          <Group grow>
            <TextInput
              label="PID"
              placeholder="device uuid"
              value={pendingFilters.pid}
              onChange={(e) => setPendingFilters({ ...pendingFilters, pid: e.currentTarget.value })}
            />
            <TextInput
              label="Username"
              value={pendingFilters.username}
              onChange={(e) =>
                setPendingFilters({ ...pendingFilters, username: e.currentTarget.value })
              }
            />
            <TextInput
              label="Org ID"
              value={pendingFilters.orgId}
              onChange={(e) =>
                setPendingFilters({ ...pendingFilters, orgId: e.currentTarget.value })
              }
            />
          </Group>
          <Group grow>
            <DateInput
              label="From"
              value={pendingFilters.from}
              onChange={(v) =>
                setPendingFilters({ ...pendingFilters, from: parseDateOnlyOrNull(v) })
              }
              clearable
              valueFormat="YYYY-MM-DD"
            />
            <DateInput
              label="To"
              value={pendingFilters.to}
              onChange={(v) => setPendingFilters({ ...pendingFilters, to: parseDateOnlyOrNull(v) })}
              clearable
              valueFormat="YYYY-MM-DD"
            />
          </Group>
          <Group>
            <Button onClick={applyFilters}>Apply</Button>
            <Button variant="outline" onClick={clearFilters}>
              Clear
            </Button>
            <Text size="sm" c="dimmed" ml="auto">
              {total} {total === 1 ? "entry" : "entries"}
            </Text>
          </Group>
        </Stack>
      </Paper>

      {error && (
        <Text c="red" mb="sm">
          {error}
        </Text>
      )}

      <Paper withBorder radius="sm" p={0}>
        <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th>Event</Table.Th>
              <Table.Th>Actor</Table.Th>
              <Table.Th>PID</Table.Th>
              <Table.Th>Username</Table.Th>
              <Table.Th>Org ID</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Center py="md">
                    <Loader size="sm" />
                  </Center>
                </Table.Td>
              </Table.Tr>
            ) : logs.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" ta="center" py="sm" size="sm">
                    No system logs match these filters
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              logs.map((log, idx) => (
                <Table.Tr key={log._id || idx}>
                  <Table.Td>
                    <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                      {log.createdAt ? new Date(log.createdAt).toLocaleString() : "-"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="blue" size="sm">
                      {log.data || "-"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {log.auto ? (
                      <Badge variant="light" color="grape" size="sm">
                        auto
                      </Badge>
                    ) : (
                      <Badge variant="light" color="teal" size="sm">
                        user
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{log.pid || "-"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{log.username || "-"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{log.orgId || "-"}</Text>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <Group justify="space-between" mt="md">
        <Text size="xs" c="dimmed">
          Page {page} of {totalPages}
        </Text>
        <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
      </Group>
    </Container>
  );
};

export default SystemLogs;
export type { SystemLogEntry };
